/* eslint-disable react-hooks/exhaustive-deps */
import { cloneDeep, forEach, get, isEmpty, isEqual, uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getClientsList } from '../../apis/finances/bank';
import useForm from '../../hooks/useForm';
import Client from '../../pages/Client/Client';
import { getParams } from '../../utils/params';
import systemAlert from '../../utils/systemAlert';
import { transformComma } from '../../utils/validator/comma';
import Button from '../Button';
import Filters from '../Filters';
import Modal, { ModalHeader, ModalTab } from '../Modal';
import { ControlTable } from '../Table';
import { dateToViewFormat } from '../../utils/date';

const CLIENT_SEARCH_OPTIONS = [
  { label: "거래처명", value: "name" },
  { label: "대표자명", value: "ceoName" },
];

const defaultForm = {
  searchType: 'name',
  searchText: '',
  page: 0,
  limit: 10,
};

const SearchClientModal = ({ open, setOpen, selected, setSelected, bankTransIdx, fields: propsFields = [] }) => {
  const { permissions } = useSelector((store) => ({
    permissions: store.Profile.permissions
  }));
  const [activeTab, setActiveTab] = useState(0);
  const [tabList, setTabList] = useState([]);
  const [filters] = useState([
    {
      title: "분류",
      type: "select",
      key: "searchType",
      options: {
        list: CLIENT_SEARCH_OPTIONS,
      },
    },
    {
      title: "키워드 검색",
      type: "input",
      key: "searchText",
      className: "input-focus",
      placeholder: "검색어 입력",
    },
  ]);
  const [fields, setFields] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [targetPermission, setTargetPermission] = useState(null);

  useEffect(() => {
    if (!isEmpty(permissions)) {
      setTargetPermission(permissions.find(p => p.code === 'COMPANY_ACCOUNT'));
    }
  }, [permissions]);

  useEffect(() => {
    setTabList([
      { label: '거래처', action: setActiveTab },
      { label: '카드사', action: setActiveTab },
      { label: '카드사용처', action: setActiveTab },
      { label: '직원', action: setActiveTab },
    ]);
  }, []);

  const {
    form,
    setForm,
    setSubmit,
    handleOnChange,
    handleSubmit,
    // LIST 용
    filterSubmit,
    list, setList,
    selectedList, setSelectedList,
    isLoading, setIsLoading,
    total, setTotal,
    page, setPage,
    limit, setLimit,
    reset
  } = useForm(defaultForm);

  useEffect(() => {
    if (open) {
      getFields(activeTab);
      setForm(defaultForm);
      submit(defaultForm);
      setSubmit(submit);
      setSelectedList([]);
      setTimeout(() => {
        const target = document.getElementsByClassName('input-focus');
        if (target && target[0]) target[0].focus();
      }, 500);
    }
  }, [open, activeTab]);

  const submit = (params) => {
    let _params = cloneDeep(params);
    switch (activeTab) {
      case 0:
        _params = {
          ..._params,
          type: ['company']
        };
        break;
      case 1:
        _params = {
          ..._params,
          type: ['cardCompany']
        };
        break;
      case 2:
        _params = {
          ..._params,
          type: ['cardUseStore']
        };
        break;
      case 3:
        _params = {
          ..._params,
          type: ['employee']
        };
        break;
      default:
        break;
    }

    getClientsList(getParams(_params))
      .then((res) => {
        setList(res.data.clientList);
        setTotal(res.data.totalCount);
      })
      .catch(() => { })
      .finally(() => setIsLoading(false));
  };

  const cancel = () => {
    setOpen(false);
    setSelectedList([]);
    setActiveTab(0);
    reset();
  };

  const getFields = (tab) => {
    switch (tab) {
      case 0:
        setFields([
          {
            title: "거래처명",
            key: "name",
          },
          {
            title: "거래처코드",
            key: 'companyAccount.code'
          },
          {
            title: "대표자명",
            key: 'companyAccount.ceoName'
          },
          {
            title: "사업자등록번호",
            key: 'companyAccount.comNum',
            transform: 'comNum'
          },
        ]);
        break;
      default:
        setFields([
          {
            title: "거래처명",
            key: "name",
          },
        ]);
        break;
    }
  };

  const handleApply = () => {
    const clientInfo = selectedList.map((list) => ({
      ...list,
      moneyIn: 0,
      moneyOut: 0
    }));


    let transList = [...selected];
    if (bankTransIdx !== null) {
      const addClients = uniqBy([
        ...transList[bankTransIdx].addClients, ...clientInfo],
        'idx'
      );
      if (addClients.length === 1) {
        addClients[0].moneyIn = transList[bankTransIdx].inAmt ? transformComma(transList[bankTransIdx].inAmt) : 0;
        addClients[0].moneyOut = transList[bankTransIdx].outAmt ? transformComma(transList[bankTransIdx].outAmt) : 0;
      }
      transList[bankTransIdx] = {
        ...transList[bankTransIdx],
        addClients
      };
    } else {
      transList = transList.map((trans) => {
        const addClients = uniqBy([...trans.addClients, ...clientInfo], 'idx');
        if (addClients.length === 1) {
          addClients[0].moneyIn = trans.inAmt ? transformComma(trans.inAmt) : 0;
          addClients[0].moneyOut = trans.outAmt ? transformComma(trans.outAmt) : 0;
        }
        return cloneDeep({
          ...trans,
          addClients
        });
      });
    }

    systemAlert('거래처 선택을 완료 하시겠습니까?', {
      buttons: [
        {
          color: 'text',
          label: '취소'
        },
        {
          label: '확인',
          onClick: () => {
            setSelected(transList);
            cancel();
          }
        }
      ]
    });
  };

  const getCheckboxDisabled = (data) => {
    let disabled = false;

    if (bankTransIdx !== null) {
      if (!isEmpty(selected[bankTransIdx]?.addClients)) {
        forEach(selected[bankTransIdx].addClients, (client) => {
          if (isEqual(client.idx, data.idx)) {
            disabled = true;
          }
        });
      }
      if (!isEmpty(selected[bankTransIdx]?.clients)) {
        forEach(selected[bankTransIdx].clients, (client) => {
          if (isEqual(client.idx, data.idx)) {
            disabled = true;
          }
        });
      }
    }

    return disabled;
  };

  return (
    <>
      <Modal isOpen={open} close={cancel}>
        <div className="select-client-mapping-modal">
          <ModalHeader>매핑 거래처 선택</ModalHeader>
          {activeTab === 0 && targetPermission && targetPermission.level > 1 && (
            <Button
              className="create-client-button"
              color="line"
              label="거래처 등록"
              onClick={() => { setDetailOpen(true); }}
            />
          )}
          <ModalTab tabList={tabList} active={activeTab} />

          {!isEmpty(propsFields) && !isEmpty(selected) && (
            <div className="client-mapping-modal mb-32">
              <div className="mapping-field">
                <table className="w-full">
                  <thead>
                    <tr>
                      {propsFields.map((field, idx) => (
                        <th key={idx}>{field.title}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selected.map((trans, transIdx) => (
                      <tr key={transIdx} style={{ height: "52px" }}>
                        <td >{dateToViewFormat(get(trans, 'tradeTime'))}</td>
                        <td className="color-red-100">{transformComma(get(trans, 'inAmt'))}</td>
                        <td className="color-purple-100">{transformComma(get(trans, 'outAmt'))}</td>
                        <td >{transformComma(get(trans, 'balanceAmt'))}</td>
                        <td >{get(trans, 'traderName')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          )}

          <Filters
            isLoading={isLoading}
            filters={filters}
            submit={filterSubmit}
            form={form}
            onChange={handleOnChange}
          />

          <ControlTable
            isControl
            isHideAllCheck
            fields={fields}
            list={list}
            isLoading={isLoading}
            total={total}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            submit={handleSubmit}
            selectedList={selectedList}
            setSelected={setSelectedList}
            checkboxDisabled={getCheckboxDisabled}
          />
          <div className="submit-button-wrap float-right margin-top-32">
            <Button
              className="button"
              size="lg"
              label="취소"
              color="text"
              action={cancel}
            />
            <Button
              className="button"
              size="lg"
              label="적용하기"
              disabled={isEmpty(selectedList)}
              action={handleApply}
            />
          </div>
        </div>
      </Modal>
      {!isEmpty(targetPermission) && (
        <Client
          permission={targetPermission}
          open={detailOpen}
          setOpen={setDetailOpen}
          submit={filterSubmit}
        />
      )}
    </>
  );
};

export default SearchClientModal;